import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  FormControl,
  FormHelperText,
  useTheme,
  useMediaQuery,
  IconButton,              // ← Added for close button
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close"; // ← Added
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Cropper from "react-easy-crop";
import { useSnackbar } from "../../App";
import { getAllBrandLists } from "../../service/brandService";
import {
  getAllCategoryLists,
  getAllSubCategory,
} from "../../service/categoryService";
import { getProductById, updateProduct } from "../../service/productService";
import Color from "../../components/Color";

// Helper to crop image to fixed size 185x140
const getCroppedImg = (imageSrc, croppedAreaPixels) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = imageSrc;
    image.crossOrigin = "anonymous";

    image.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      canvas.width = 185;
      canvas.height = 140;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        185,
        140
      );

      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error("Canvas is empty"));
          const file = new File([blob], "cropped.png", { type: "image/png" });
          const url = URL.createObjectURL(blob);
          resolve({ file, url });
        },
        "image/png",
        1.0
      );
    };

    image.onerror = (err) => reject(err);
  });
};

const EditProduct = ({ open, onClose, productId, onUpdate }) => {
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const nameInputRef = useRef(null);
  const shortCodeInputRef = useRef(null);
  const hsnCodeInputRef = useRef(null);
  const gstPercentageInputRef = useRef(null);
  const priceInputRef = useRef(null);
  const unitsQuantityInputRef = useRef(null);
  const unitsRef = useRef(null);
  const subCategoryRef = useRef(null);
  const categoryRef = useRef(null);
  const descriptionRef = useRef(null);

  const initialFormState = {
    name: "",
    short_code: "",
    hsn_code: "",
    gst_percentage: "",
    base_price: "",
    brand_id: "",
    category_id: "",
    description: "",
    is_active: true,
    sub_category_id: "",
    units: "",
    unit_quantity: "",
    is_favourite: false,
  };

  const [form, setForm] = useState(initialFormState);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [rawImageUrl, setRawImageUrl] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !productId) return;

    const fetchDropdowns = async () => {
      try {
        const [brandRes, categoryRes] = await Promise.all([
          getAllBrandLists(),
          getAllCategoryLists(),
        ]);

        setBrands(Array.isArray(brandRes?.data?.data) ? brandRes.data.data : []);
        setCategories(Array.isArray(categoryRes?.data?.data) ? categoryRes.data.data : []);
      } catch {
        showSnackbar("Failed to load dropdown data", "error");
      }
    };

    const fetchProduct = async () => {
      try {
        const res = await getProductById(productId);
        const product = res.data.data;

        setForm({
          name: product.name || "",
          short_code: product.short_code || "",
          hsn_code: product.hsn_code || "",
          gst_percentage: product.gst_percentage != null ? product.gst_percentage : (product.tax_rate != null ? product.tax_rate : ""),
          base_price: (product.price_includes_tax !== false ? (product.inclusive_price ?? product.base_price) : product.base_price) || "",

          brand_id: product.brand_id || "",
          category_id: product.category_id || "",
          description: product.description || "",
          is_active: product.is_active == 1 ? true : false,
          sub_category_id: product.sub_category_id || "",
          units: (product.units === "packet" ? "pkt" : product.units) || "",
          unit_quantity: product.unit_quantity || "",
          is_favourite: product.is_favourite == 1 ? true : false,
        });

        setUploadedImageUrl(product.image_url || null);
        setErrors({});
        setImageFile(null);
        setRawImageUrl(null);
        setCropDialogOpen(false);
        setCroppedAreaPixels(null);
      } catch {
        showSnackbar("Failed to fetch product details", "error");
      }
    };

    fetchDropdowns();
    fetchProduct();
  }, [open, productId, showSnackbar]);

  useEffect(() => {
    if (!form.category_id) {
      setSubCategories([]);
      return;
    }

    const fetchSubCategories = async () => {
      try {
        const res = await getAllSubCategory(form.category_id);
        setSubCategories(Array.isArray(res?.data?.data) ? res.data.data : []);
      } catch {
        showSnackbar("Failed to load subcategories", "error");
      }
    };

    fetchSubCategories();
  }, [form.category_id, showSnackbar]);

  const handleCancel = () => {
    setForm(initialFormState);
    setErrors({});
    setImageFile(null);
    setUploadedImageUrl(null);
    setRawImageUrl(null);
    setCropDialogOpen(false);
    onClose();
  };

  const handleCropCancel = () => {
    setCropDialogOpen(false);
    setRawImageUrl(null);
  };

  const handleChange = (field, value) => {
    if (field === "name") {
      const limitedValue = value.slice(0, 100);
      setForm((prev) => ({ ...prev, name: limitedValue }));

      if (!limitedValue.trim()) {
        setErrors((prev) => ({ ...prev, name: ["Product name is required"] }));
      } else if (limitedValue.length > 100) {
        setErrors((prev) => ({
          ...prev,
          name: ["Product name must not exceed 100 characters"],
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.name;
          return newErrors;
        });
      }
    } else if (field === "short_code") {
      const upperValue = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
      const limitedValue = upperValue.slice(0, 20);
      setForm((prev) => ({ ...prev, short_code: limitedValue }));

      if (!limitedValue.trim()) {
        setErrors((prev) => ({
          ...prev,
          short_code: ["Product code is required"],
        }));
      } else if (limitedValue.length > 20) {
        setErrors((prev) => ({
          ...prev,
          short_code: ["Short code must not exceed 20 characters"],
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.short_code;
          return newErrors;
        });
      }
    } else if (field === "hsn_code") {
      const upperValue = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
      const limitedValue = upperValue.slice(0, 20);
      setForm((prev) => ({ ...prev, hsn_code: limitedValue }));

      if (limitedValue.length > 20) {
        setErrors((prev) => ({
          ...prev,
          hsn_code: ["HSN code must not exceed 20 characters"],
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.hsn_code;
          return newErrors;
        });
      }
    } else if (field === "gst_percentage") {
      const sanitized = value.replace(/[^0-9.]/g, "");
      setForm((prev) => ({ ...prev, gst_percentage: sanitized }));

      if (sanitized !== "" && (parseFloat(sanitized) < 0 || parseFloat(sanitized) > 100)) {
        setErrors((prev) => ({
          ...prev,
          gst_percentage: ["GST % must be between 0 and 100"],
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.gst_percentage;
          return newErrors;
        });
      }
    } else if (field === "description") {
      const limitedValue = value.slice(0, 500);
      setForm((prev) => ({ ...prev, description: limitedValue }));

      if (limitedValue.length > 500) {
        setErrors((prev) => ({
          ...prev,
          description: ["Description must not exceed 500 characters"],
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.description;
          return newErrors;
        });
      }

    } else if (["base_price", "unit_quantity"].includes(field)) {
      // For base_price and unit_quantity, decide if decimals are allowed.
      // Current requirement only mentions tax fields, so keeping these as is
      // but separating them in case price needs decimals later.
      const numericValue = value.replace(/[^0-9.]/g, "").replace(/(\..*?)\..*/g, '$1');
      let maxLength = 10;
      if (field === "unit_quantity") maxLength = 7; // Increased to accommodate decimal point
      const limitedValue = numericValue.slice(0, maxLength);
      setForm((prev) => ({ ...prev, [field]: limitedValue }));

      if (field === "base_price") {
        if (!limitedValue) {
          setErrors((prev) => ({ ...prev, base_price: ["Price is required"] }));
        } else if (parseFloat(limitedValue) <= 0) {
          setErrors((prev) => ({
            ...prev,
            base_price: ["Price must be greater than 0"],
          }));
        } else {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.base_price;
            return newErrors;
          });
        }
      } else if (field === "unit_quantity") {
        if (!limitedValue) {
          setErrors((prev) => ({
            ...prev,
            unit_quantity: ["Quantity is required"],
          }));
        } else if (parseInt(limitedValue, 10) <= 0) {
          setErrors((prev) => ({
            ...prev,
            unit_quantity: ["Quantity must be greater than 0"],
          }));
        } else {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.unit_quantity;
            return newErrors;
          });
        }
      }
    } else if (field === "units") {
      const mappedValue = value === "packet" ? "pkt" : value;
      setForm((prev) => ({ ...prev, units: mappedValue }));
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.units;
        return newErrors;
      });
    } else if (field === "category_id") {
      setForm((prev) => ({ ...prev, category_id: value, sub_category_id: "" }));
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.category_id;
        delete newErrors.sub_category_id;
        return newErrors;
      });
    } else {
      setForm((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showSnackbar("Please upload a valid image file", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showSnackbar("Image size must be less than 5MB", "error");
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.src = objectUrl;

    img.onload = () => {
      if (img.width === 185 && img.height === 140) {
        setImageFile(file);
        setUploadedImageUrl(objectUrl);
      } else {
        const reader = new FileReader();
        reader.onload = () => {
          setRawImageUrl(reader.result);
          setCropDialogOpen(true);
        };
        reader.readAsDataURL(file);
      }
      URL.revokeObjectURL(objectUrl);
    };

    img.onerror = () => {
      showSnackbar("Failed to read image", "error");
      URL.revokeObjectURL(objectUrl);
    };
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleCropConfirm = async () => {
    try {
      const { file, url } = await getCroppedImg(rawImageUrl, croppedAreaPixels);
      setImageFile(file);
      setUploadedImageUrl(url);
      setCropDialogOpen(false);
      setRawImageUrl(null);
    } catch {
      showSnackbar("Failed to crop image", "error");
    }
  };

  const validateForm = (data) => {
    const newErrors = {};

    if (!data.name.trim()) {
      newErrors.name = ["Product name is required"];
    } else if (data.name.length > 100) {
      newErrors.name = ["Product name must not exceed 100 characters"];
    }

    if (!data.short_code.trim()) {
      newErrors.short_code = ["Product code is required"];
    } else if (data.short_code.length > 20) {
      newErrors.short_code = ["Short code must not exceed 20 characters"];
    }

    if (data.hsn_code && data.hsn_code.length > 20) {
      newErrors.hsn_code = ["HSN code must not exceed 20 characters"];
    }

    if (data.gst_percentage !== "" && data.gst_percentage !== null && data.gst_percentage !== undefined) {
      const gstVal = parseFloat(data.gst_percentage);
      if (isNaN(gstVal) || gstVal < 0 || gstVal > 100) {
        newErrors.gst_percentage = ["GST % must be between 0 and 100"];
      }
    }

    if (!data.base_price || parseFloat(data.base_price) <= 0) {
      newErrors.base_price = ["Price must be greater than 0"];
    } else if (data.base_price.length > 10) {
      newErrors.base_price = ["Price must not exceed 10 digits"];
    }

    if (!data.category_id) {
      newErrors.category_id = ["Category is required"];
    }

    if (!data.units || !["kg", "g", "piece", "pkt", "packet", "number"].includes(data.units)) {
      newErrors.units = ["Valid unit type is required"];
    }

    if (!data.unit_quantity || parseInt(data.unit_quantity, 10) <= 0) {
      newErrors.unit_quantity = ["Quantity must be greater than 0"];
    } else if (data.unit_quantity.length > 6) {
      newErrors.unit_quantity = ["Quantity must not exceed 6 digits"];
    }

    const selectedCategory = categories.find((c) => c.id === data.category_id);
    if (
      selectedCategory?.name?.toLowerCase() === "cake" &&
      !data.sub_category_id
    ) {
      newErrors.sub_category_id = ["Subcategory is required for cake products"];
    }

    if (data.description && data.description.length > 500) {
      newErrors.description = ["Description must not exceed 500 characters"];
    }



    return newErrors;
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (isSubmitting) return;

    const newErrors = validateForm(form);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      showSnackbar("Please fix validation errors before submitting.", "error");

      const fieldOrder = [
        "name",
        "short_code",
        "hsn_code",
        "gst_percentage",
        "base_price",
        "unit_quantity",
        "units",
        "category_id",
        "sub_category_id",
        "description",
      ];

      const fieldRefs = {
        name: nameInputRef,
        short_code: shortCodeInputRef,
        hsn_code: hsnCodeInputRef,
        gst_percentage: gstPercentageInputRef,
        base_price: priceInputRef,
        unit_quantity: unitsQuantityInputRef,
        units: unitsRef,
        category_id: categoryRef,
        sub_category_id: subCategoryRef,
        description: descriptionRef,
      };

      for (const field of fieldOrder) {
        if (newErrors[field]) {
          fieldRefs[field]?.current?.focus();
          break;
        }
      }
      return;
    }

    setIsSubmitting(true);
    try {
      let response;
      let payload;
      let isMultipart = !!imageFile;

      if (isMultipart) {
        payload = new FormData();
        Object.entries(form).forEach(([key, value]) => {
          if (["is_active", "is_favourite"].includes(key)) {
            payload.append(key, value ? 1 : 0);
          } else {
            payload.append(key, value);
          }
        });
        payload.append("price_includes_tax", 1);
        payload.append("inclusive_price", form.base_price);
        payload.append("image", imageFile);
      } else {
        payload = {
          ...form,
          price_includes_tax: 1,
          inclusive_price: form.base_price,
          is_active: form.is_active ? 1 : 0,
          is_favourite: form.is_favourite ? 1 : 0,
        };
      }

      response = await updateProduct(productId, payload, isMultipart);
      const resData = response.data;

      if (resData.status === "error") {
        setErrors(resData.errors || {});
        showSnackbar(resData.message || "Validation failed", "error");

        // Focus first error field
        const fieldOrder = ["name", "short_code", "hsn_code", "gst_percentage", "base_price", "unit_quantity", "units", "category_id", "sub_category_id", "description"];
        const fieldRefs = {
          name: nameInputRef,
          short_code: shortCodeInputRef,
          hsn_code: hsnCodeInputRef,
          gst_percentage: gstPercentageInputRef,
          base_price: priceInputRef,
          unit_quantity: unitsQuantityInputRef,
          units: unitsRef,
          category_id: categoryRef,
          sub_category_id: subCategoryRef,
          description: descriptionRef,
        };
        for (const field of fieldOrder) {
          if (resData.errors?.[field]) {
            fieldRefs[field]?.current?.focus();
            break;
          }
        }
        return;
      }

      showSnackbar("Product updated successfully!", "success");
      if (onUpdate) onUpdate(resData.data);

      // Close modal after success
      setTimeout(() => onClose(), 800);
    } catch (err) {
      console.error("Update failed:", err);
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
        showSnackbar("Validation failed. Please check the form.", "error");

        // Focus first error field from backend errors
        const fieldOrder = ["name", "short_code", "hsn_code", "base_price", "unit_quantity", "units", "category_id", "sub_category_id", "description"];
        const fieldRefs = {
          name: nameInputRef,
          short_code: shortCodeInputRef,
          hsn_code: hsnCodeInputRef,
          base_price: priceInputRef,
          unit_quantity: unitsQuantityInputRef,
          units: unitsRef,
          category_id: categoryRef,
          sub_category_id: subCategoryRef,
          description: descriptionRef,
        };
        for (const field of fieldOrder) {
          if (err.response.data.errors[field]) {
            fieldRefs[field]?.current?.focus();
            break;
          }
        }
      } else {
        showSnackbar(err.response?.data?.message || "Something went wrong while updating the product", "error");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Main Edit Product Dialog */}
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (isSubmitting) return;
          handleCancel();
        }}
        disableEscapeKeyDown={isSubmitting}
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: { xs: "95vw", sm: "600px", md: "720px" },
            borderRadius: "20px",
            margin: { xs: 1, sm: 2 },
            boxShadow: "0 24px 48px -12px rgba(0,0,0,0.12)",
            border: "1px solid rgba(0, 0, 0, 0.05)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: isMobile ? "1.1rem" : "1.35rem",
            fontWeight: "700",
            letterSpacing: "-0.02em",
            position: "relative",
            pr: 6,
            py: 2.5,
            px: 4,
            background: "linear-gradient(135deg, rgba(208, 2, 27, 0.03) 0%, rgba(124, 11, 204, 0.03) 100%)",
            borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
          }}
        >
          Edit Product

          <IconButton
            aria-label="close"
            onClick={handleCancel}
            disabled={isSubmitting}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: "text.secondary",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                color: Color.primary.main,
                backgroundColor: "rgba(208, 2, 27, 0.08)",
                transform: "rotate(90deg)",
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers={false}
          sx={{
            px: 4,
            py: 3,
            maxHeight: { xs: "70vh", sm: "75vh", md: "80vh" },
            overflowY: "auto",
            backgroundColor: "#ffffff",
          }}
        >
          <Box component="form" onSubmit={handleSubmit}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
                rowGap: 1.5,
                columnGap: 2.5,
                pt: 1.5,
              }}
            >
              {/* Product Name */}
              <Box sx={{ gridColumn: { md: "span 2", sm: "span 2", xs: "span 1" } }}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 0.5,
                    fontWeight: "600",
                    color: Color.text.primary,
                    fontSize: "13px",
                  }}
                >
                  Product Name*
                </Typography>
                <TextField
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Enter product name"
                  error={!!errors.name}
                  helperText={errors.name?.[0] || `${form.name.length}/100`}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  inputProps={{ maxLength: 100 }}
                  inputRef={nameInputRef}
                  sx={{
                    "& .MuiInputLabel-root": { display: "none" },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      backgroundColor: "#fcfcfd",
                      transition: "all 0.2s ease-in-out",
                      "& fieldset": {
                        borderColor: "rgba(0, 0, 0, 0.08)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(0, 0, 0, 0.2)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: Color.primary.main,
                        borderWidth: "1.5px",
                        boxShadow: `0 0 0 3px rgba(208, 2, 27, 0.1)`,
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: isMobile ? "0.85rem" : "0.95rem",
                      py: 1.5,
                    },
                  }}
                />
              </Box>

              {/* Short Code */}
              <Box sx={{ gridColumn: { md: "span 1", sm: "span 1", xs: "span 1" } }}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 0.5,
                    fontWeight: "600",
                    color: Color.text.primary,
                    fontSize: "13px",
                  }}
                >
                  Short Code*
                </Typography>
                <TextField
                  value={form.short_code}
                  onChange={(e) => handleChange("short_code", e.target.value)}
                  placeholder="e.g. NM001"
                  error={!!errors.short_code}
                  helperText={errors.short_code?.[0] || `${form.short_code.length}/20`}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  inputProps={{ maxLength: 20 }}
                  inputRef={shortCodeInputRef}
                  sx={{
                    "& .MuiInputLabel-root": { display: "none" },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      backgroundColor: "#fcfcfd",
                      transition: "all 0.2s ease-in-out",
                      "& fieldset": {
                        borderColor: "rgba(0, 0, 0, 0.08)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(0, 0, 0, 0.2)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: Color.primary.main,
                        borderWidth: "1.5px",
                        boxShadow: `0 0 0 3px rgba(208, 2, 27, 0.1)`,
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: isMobile ? "0.85rem" : "0.95rem",
                      py: 1.5,
                      textTransform: "uppercase",
                    },
                  }}
                />
              </Box>

              {/* Price */}
              <Box sx={{ gridColumn: { md: "span 1", sm: "span 1", xs: "span 1" } }}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 0.5,
                    fontWeight: "600",
                    color: Color.text.primary,
                    fontSize: "13px",
                  }}
                >
                  Price (Incl. GST)*
                </Typography>
                <TextField
                  type="text"
                  inputMode="decimal"
                  value={form.base_price}
                  onChange={(e) => handleChange("base_price", e.target.value)}
                  placeholder="Enter price"
                  error={!!errors.base_price}
                  helperText={errors.base_price?.[0] || `${form.base_price.length}/10`}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  inputProps={{ maxLength: 10 }}
                  inputRef={priceInputRef}
                  sx={{
                    "& .MuiInputLabel-root": { display: "none" },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      backgroundColor: "#fcfcfd",
                      transition: "all 0.2s ease-in-out",
                      "& fieldset": {
                        borderColor: "rgba(0, 0, 0, 0.08)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(0, 0, 0, 0.2)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: Color.primary.main,
                        borderWidth: "1.5px",
                        boxShadow: `0 0 0 3px rgba(208, 2, 27, 0.1)`,
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: isMobile ? "0.85rem" : "0.95rem",
                      py: 1.5,
                    },
                  }}
                />
              </Box>

              {/* Unit Quantity */}
              <Box sx={{ gridColumn: { md: "span 1", sm: "span 1", xs: "span 1" } }}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 0.5,
                    fontWeight: "600",
                    color: Color.text.primary,
                    fontSize: "13px",
                  }}
                >
                  Unit Quantity*
                </Typography>
                <TextField
                  type="text"
                  inputMode="decimal"
                  value={form.unit_quantity}
                  onChange={(e) => handleChange("unit_quantity", e.target.value)}
                  placeholder="e.g. 500"
                  error={!!errors.unit_quantity}
                  helperText={errors.unit_quantity?.[0] || `${form.unit_quantity.length}/6`}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  inputProps={{ maxLength: 6 }}
                  inputRef={unitsQuantityInputRef}
                  sx={{
                    "& .MuiInputLabel-root": { display: "none" },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      backgroundColor: "#fcfcfd",
                      transition: "all 0.2s ease-in-out",
                      "& fieldset": {
                        borderColor: "rgba(0, 0, 0, 0.08)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(0, 0, 0, 0.2)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: Color.primary.main,
                        borderWidth: "1.5px",
                        boxShadow: `0 0 0 3px rgba(208, 2, 27, 0.1)`,
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: isMobile ? "0.85rem" : "0.95rem",
                      py: 1.5,
                    },
                  }}
                />
              </Box>

              {/* Unit Type */}
              <Box sx={{ gridColumn: { md: "span 1", sm: "span 1", xs: "span 1" } }}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 0.5,
                    fontWeight: "600",
                    color: Color.text.primary,
                    fontSize: "13px",
                  }}
                >
                  Unit Type*
                </Typography>
                <FormControl fullWidth error={!!errors.units}>
                  <Select
                    value={form.units || ""}
                    onChange={(e) => handleChange("units", e.target.value)}
                    displayEmpty
                    ref={unitsRef}
                    sx={{
                      borderRadius: "10px",
                      backgroundColor: "#fcfcfd",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0, 0, 0, 0.08)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0, 0, 0, 0.2)",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: Color.primary.main,
                        borderWidth: "1.5px",
                      },
                      fontSize: isMobile ? "0.85rem" : "0.95rem",
                      height: 48,
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select unit
                    </MenuItem>
                    <MenuItem value="kg">kg</MenuItem>
                    <MenuItem value="g">gram</MenuItem>
                    <MenuItem value="piece">piece</MenuItem>
                    <MenuItem value="pkt">packet</MenuItem>
                    <MenuItem value="number">number</MenuItem>
                  </Select>
                  {errors.units && <FormHelperText>{errors.units[0]}</FormHelperText>}
                </FormControl>
              </Box>

              {/* Brand */}
              <Box sx={{ gridColumn: { md: "span 1", sm: "span 1", xs: "span 1" } }}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 0.5,
                    fontWeight: "600",
                    color: Color.text.primary,
                    fontSize: "13px",
                  }}
                >
                  Brand (Optional)
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={form.brand_id ?? ""}
                    onChange={(e) => handleChange("brand_id", e.target.value)}
                    displayEmpty
                    sx={{
                      borderRadius: "10px",
                      backgroundColor: "#fcfcfd",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0, 0, 0, 0.08)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0, 0, 0, 0.2)",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: Color.primary.main,
                        borderWidth: "1.5px",
                      },
                      fontSize: isMobile ? "0.85rem" : "0.95rem",
                      height: 48,
                    }}
                  >
                    <MenuItem value="">Select brand</MenuItem>
                    {brands.map((brand) => (
                      <MenuItem key={brand.id} value={brand.id}>
                        {brand.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Category */}
              <Box sx={{ gridColumn: { md: "span 1", sm: "span 1", xs: "span 1" } }}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 0.5,
                    fontWeight: "600",
                    color: Color.text.primary,
                    fontSize: "13px",
                  }}
                >
                  Category*
                </Typography>
                <FormControl fullWidth error={!!errors.category_id}>
                  <Select
                    value={form.category_id ?? ""}
                    onChange={(e) => handleChange("category_id", e.target.value)}
                    displayEmpty
                    ref={categoryRef}
                    sx={{
                      borderRadius: "10px",
                      backgroundColor: "#fcfcfd",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0, 0, 0, 0.08)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0, 0, 0, 0.2)",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: Color.primary.main,
                        borderWidth: "1.5px",
                      },
                      fontSize: isMobile ? "0.85rem" : "0.95rem",
                      height: 48,
                    }}
                  >
                    <MenuItem value="">Select category</MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.category_id && (
                    <FormHelperText>{errors.category_id[0]}</FormHelperText>
                  )}
                </FormControl>
              </Box>

              {/* HSN Code */}
              <Box sx={{ gridColumn: { md: "span 1", sm: "span 1", xs: "span 1" } }}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 0.5,
                    fontWeight: "600",
                    color: Color.text.primary,
                    fontSize: "13px",
                  }}
                >
                  HSN Code (Optional)
                </Typography>
                <TextField
                  value={form.hsn_code}
                  onChange={(e) => handleChange("hsn_code", e.target.value)}
                  placeholder="e.g. 1905"
                  error={!!errors.hsn_code}
                  helperText={errors.hsn_code?.[0] || `${form.hsn_code.length}/20`}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  inputRef={hsnCodeInputRef}
                  inputProps={{ maxLength: 20 }}
                  sx={{
                    "& .MuiInputLabel-root": { display: "none" },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      backgroundColor: "#fcfcfd",
                      transition: "all 0.2s ease-in-out",
                      "& fieldset": {
                        borderColor: "rgba(0, 0, 0, 0.08)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(0, 0, 0, 0.2)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: Color.primary.main,
                        borderWidth: "1.5px",
                        boxShadow: `0 0 0 3px rgba(208, 2, 27, 0.1)`,
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: isMobile ? "0.85rem" : "0.95rem",
                      py: 1.5,
                      textTransform: "uppercase",
                    },
                  }}
                />
              </Box>

              {/* GST % */}
              <Box sx={{ gridColumn: { md: "span 1", sm: "span 1", xs: "span 1" } }}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 0.5,
                    fontWeight: "600",
                    color: Color.text.primary,
                    fontSize: "13px",
                  }}
                >
                  GST % (Optional)
                </Typography>
                <TextField
                  type="text"
                  inputMode="decimal"
                  value={form.gst_percentage}
                  onChange={(e) => handleChange("gst_percentage", e.target.value)}
                  placeholder="e.g. 5"
                  error={!!errors.gst_percentage}
                  helperText={errors.gst_percentage?.[0] || "e.g. 0, 5, 12, 18"}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  inputRef={gstPercentageInputRef}
                  sx={{
                    "& .MuiInputLabel-root": { display: "none" },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      backgroundColor: "#fcfcfd",
                      transition: "all 0.2s ease-in-out",
                      "& fieldset": {
                        borderColor: "rgba(0, 0, 0, 0.08)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(0, 0, 0, 0.2)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: Color.primary.main,
                        borderWidth: "1.5px",
                        boxShadow: `0 0 0 3px rgba(208, 2, 27, 0.1)`,
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: isMobile ? "0.85rem" : "0.95rem",
                      py: 1.5,
                    },
                  }}
                />
              </Box>

              {/* Conditional Subcategory + Description */}
              {(() => {
                const isCake =
                  categories.find((c) => c.id === form.category_id)?.name?.toLowerCase() ===
                  "cake";

                if (isCake) {
                  return (
                    <>
                      <Box sx={{ gridColumn: { md: "span 1", sm: "span 1", xs: "span 1" } }}>
                        <Typography
                          variant="body1"
                          sx={{
                            mb: 0.5,
                            fontWeight: "600",
                            color: Color.text.primary,
                            fontSize: "13px",
                          }}
                        >
                          Subcategory*
                        </Typography>
                        <FormControl fullWidth error={!!errors.sub_category_id}>
                          <Select
                            value={form.sub_category_id ?? ""}
                            onChange={(e) =>
                              handleChange("sub_category_id", e.target.value)
                            }
                            displayEmpty
                            ref={subCategoryRef}
                            sx={{
                              borderRadius: "10px",
                              backgroundColor: "#fcfcfd",
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(0, 0, 0, 0.08)",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "rgba(0, 0, 0, 0.2)",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: Color.primary.main,
                                borderWidth: "1.5px",
                              },
                              fontSize: isMobile ? "0.85rem" : "0.95rem",
                              height: 48,
                            }}
                          >
                            <MenuItem value="" disabled>
                              Select subcategory
                            </MenuItem>
                            {subCategories.map((sub) => (
                              <MenuItem key={sub.id} value={sub.id}>
                                {sub.name}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.sub_category_id && (
                            <FormHelperText>{errors.sub_category_id[0]}</FormHelperText>
                          )}
                        </FormControl>
                      </Box>

                      <Box sx={{ gridColumn: { md: "span 1", sm: "span 1", xs: "span 1" } }}>
                        <Typography
                          variant="body1"
                          sx={{
                            mb: 0.5,
                            fontWeight: "600",
                            color: Color.text.primary,
                            fontSize: "13px",
                          }}
                        >
                          Description (Optional)
                        </Typography>
                        <TextField
                          multiline
                          rows={4}
                          value={form.description}
                          onChange={(e) => handleChange("description", e.target.value)}
                          placeholder="Enter description"
                          error={!!errors.description}
                          helperText={
                            errors.description?.[0] || `${form.description.length}/500`
                          }
                          fullWidth
                          variant="outlined"
                          InputLabelProps={{ shrink: false }}
                          inputProps={{ maxLength: 500 }}
                          inputRef={descriptionRef}
                          sx={{
                            "& .MuiInputLabel-root": { display: "none" },
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "10px",
                              backgroundColor: "#fcfcfd",
                              transition: "all 0.2s ease-in-out",
                              "& fieldset": {
                                borderColor: "rgba(0, 0, 0, 0.08)",
                              },
                              "&:hover fieldset": {
                                borderColor: "rgba(0, 0, 0, 0.2)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: Color.primary.main,
                                borderWidth: "1.5px",
                                boxShadow: `0 0 0 3px rgba(208, 2, 27, 0.1)`,
                              },
                            },
                            "& .MuiInputBase-inputMultiline": {
                              fontSize: isMobile ? "0.85rem" : "0.95rem",
                            },
                          }}
                        />
                      </Box>
                    </>
                  );
                }

                return (
                  <Box sx={{ gridColumn: { md: "span 2", sm: "span 2", xs: "span 1" } }}>
                    <Typography
                      variant="body1"
                      sx={{
                        mb: 0.5,
                        fontWeight: "600",
                        color: Color.text.primary,
                        fontSize: "13px",
                      }}
                    >
                      Description (Optional)
                    </Typography>
                    <TextField
                      multiline
                      rows={4}
                      value={form.description}
                      onChange={(e) => handleChange("description", e.target.value)}
                      placeholder="Enter description"
                      error={!!errors.description}
                      helperText={
                        errors.description?.[0] || `${form.description.length}/500`
                      }
                      fullWidth
                      variant="outlined"
                      InputLabelProps={{ shrink: false }}
                      inputProps={{ maxLength: 500 }}
                      inputRef={descriptionRef}
                      sx={{
                        "& .MuiInputLabel-root": { display: "none" },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          backgroundColor: "#fcfcfd",
                          transition: "all 0.2s ease-in-out",
                          "& fieldset": {
                            borderColor: "rgba(0, 0, 0, 0.08)",
                          },
                          "&:hover fieldset": {
                            borderColor: "rgba(0, 0, 0, 0.2)",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: Color.primary.main,
                            borderWidth: "1.5px",
                            boxShadow: `0 0 0 3px rgba(208, 2, 27, 0.1)`,
                          },
                        },
                        "& .MuiInputBase-inputMultiline": {
                          fontSize: isMobile ? "0.85rem" : "0.95rem",
                        },
                      }}
                    />
                  </Box>
                );
              })()}
            </Box>

            {/* Premium Divider */}
            <Box sx={{ borderBottom: "1px solid rgba(0, 0, 0, 0.06)", my: 1.5 }} />

            {/* Status & Favourite Switches (Styled as Cards) */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mb: 3,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              {/* Status Switch Card */}
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  borderRadius: "12px",
                  border: "1px solid rgba(0, 0, 0, 0.06)",
                  backgroundColor: "rgba(0, 0, 0, 0.01)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "rgba(0, 0, 0, 0.12)",
                    backgroundColor: "rgba(0, 0, 0, 0.02)",
                  }
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5, color: Color.text.primary }}>
                    Product Status
                  </Typography>
                  
                </Box>
                <Switch
                  checked={form.is_active}
                  onChange={(e) => handleChange("is_active", e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: Color.primary.main },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: Color.primary.main,
                    },
                  }}
                />
              </Box>

              {/* Favourite Switch Card */}
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  borderRadius: "12px",
                  border: "1px solid rgba(0, 0, 0, 0.06)",
                  backgroundColor: "rgba(0, 0, 0, 0.01)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "rgba(0, 0, 0, 0.12)",
                    backgroundColor: "rgba(0, 0, 0, 0.02)",
                  }
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5, color: Color.text.primary }}>
                    Mark as Favourite
                  </Typography>
                </Box>
                <Switch
                  checked={form.is_favourite}
                  onChange={(e) => handleChange("is_favourite", e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: Color.primary.main },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: Color.primary.main,
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Image Section */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: uploadedImageUrl ? "1fr 1fr" : "1fr" },
                gap: 3,
                mt: 3,
              }}
            >
              {/* Image Upload Card */}
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1.5,
                    fontWeight: "bold",
                    color: Color.text.primary,
                  }}
                >
                  Product Image (Optional)
                </Typography>
                
                <Box
                  component="label"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed",
                    borderColor: "rgba(0, 0, 0, 0.12)",
                    borderRadius: "12px",
                    p: 3,
                    backgroundColor: "rgba(0, 0, 0, 0.01)",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    textAlign: "center",
                    "&:hover": {
                      borderColor: Color.primary.main,
                      backgroundColor: "rgba(208, 2, 27, 0.02)",
                      transform: "scale(1.01)",
                    }
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 32, color: "text.secondary", mb: 1 }} />
                  <Typography variant="body2" sx={{ fontWeight: "600", color: Color.primary.main }}>
                    Click to upload image
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Recommended: 185×140 pixels (Max 5MB)
                  </Typography>
                  <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
                </Box>
              </Box>

              {/* Uploaded Preview Card */}
              {uploadedImageUrl && (
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1.5,
                      fontWeight: "bold",
                      color: Color.text.primary,
                    }}
                  >
                    Image Preview
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: "280px",
                      height: "140px",
                      borderRadius: "12px",
                      overflow: "hidden",
                      border: "1px solid rgba(0,0,0,0.08)",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                      position: "relative",
                    }}
                  >
                    <img
                      src={uploadedImageUrl}
                      alt="Product preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Box>

            {/* Actions Buttons */}
            <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
              <Button
                type="button"
                onClick={handleCancel}
                sx={{
                  background: "none",
                  color: Color.secondary.main,
                  border: `1px solid ${Color.secondary.main}`,
                  borderRadius: "10px",
                  width: isMobile ? "100%" : "auto",
                  height: "40px",
                  fontSize: "0.9rem",
                  fontWeight: "600",
                  textTransform: "none",
                  padding: "8px 20px",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(124, 11, 204, 0.04)",
                    borderColor: Color.secondary.main,
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                sx={{
                  background: Color.primary.main,
                  color: "white",
                  borderRadius: "10px",
                  width: isMobile ? "100%" : "auto",
                  height: "40px",
                  fontSize: "0.9rem",
                  fontWeight: "600",
                  textTransform: "none",
                  padding: "8px 24px",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background: Color.primary.dark,
                  },
                  "&.Mui-disabled": {
                    background: Color.primary.main,
                    opacity: 0.7,
                    color: "white",
                  },
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Update Product"
                )}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Crop Dialog */}
      <Dialog open={cropDialogOpen} onClose={handleCropCancel} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            position: "relative",
            pr: 6,
          }}
        >
          Crop Image to 185×140

          <IconButton
            aria-label="close"
            onClick={handleCropCancel}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ position: "relative", height: 400 }}>
          {rawImageUrl && (
            <Cropper
              image={rawImageUrl}
              crop={crop}
              zoom={zoom}
              aspect={185 / 140}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCropCancel}>Cancel</Button>
          <Button
            onClick={handleCropConfirm}
            variant="contained"
            sx={{
              background: Color.primary.main,
              "&:hover": { background: Color.primary.dark },
            }}
          >
            Confirm Crop
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditProduct;